import { Link, Outlet, getRouteApi, useLocation, useNavigate } from "@tanstack/react-router"
import { Pause, Play } from "lucide-react"
import type { KeyboardEvent } from "react"

import { useAuth } from "../../hooks/use-auth"
import { useScheduleActions } from "../../hooks/use-schedule-actions"
import { cadenceLabel, scheduledJobLabel } from "../../lib/scheduled-job-display"
import {
  useScheduledJobRevisions,
  useScheduledJobRuns,
  type ScheduledJob,
} from "../../services/use-scheduled-jobs"
import { ScheduleJobStatus } from "./schedule-job-status"
import { ScheduleRunHealth } from "./schedule-run-health"
import { ScheduleRunNow } from "./schedule-run-now"

const TAB_PATHS = {
  runs: "/settings/schedules/$jobId",
  schedule: "/settings/schedules/$jobId/schedule",
  history: "/settings/schedules/$jobId/history",
} as const
const route = getRouteApi("/settings/schedules/$jobId")

export function ScheduleJobDetail({ job }: { job: ScheduledJob }) {
  const auth = useAuth()
  const search = route.useSearch()
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })
  const active = activeTab(pathname)
  const runs = useScheduledJobRuns(job.id)
  const revisions = useScheduledJobRevisions(job.id)
  const actions = useScheduleActions()
  const tabs = [
    { id: "runs", label: "Runs", count: runs.data?.length },
    { id: "schedule", label: "Schedule" },
    { id: "history", label: "Change history", count: revisions.data?.length },
  ] as const
  const selectTab = (tab: keyof typeof TAB_PATHS) =>
    void navigate({ to: TAB_PATHS[tab], params: { jobId: job.id }, search })
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const offset = ({ ArrowRight: 1, ArrowLeft: -1 } as Record<string, number>)[event.key] ?? 0
    if (!offset && event.key !== "Home" && event.key !== "End") {
      return
    }
    event.preventDefault()
    const nextIndex =
      ({ Home: 0, End: 2 } as Record<string, number>)[event.key] ??
      (index + offset + tabs.length) % tabs.length
    const next = tabs[nextIndex]
    if (!next) {
      return
    }
    selectTab(next.id)
    document.getElementById(`tab-${next.id}`)?.focus()
  }
  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/settings">Settings</Link>

        <span aria-hidden="true">/</span>

        <Link to="/settings/schedules" search={search}>
          Schedules
        </Link>

        <span aria-hidden="true">/</span>

        <span aria-current="page" className="od-truncate text-foreground">
          {scheduledJobLabel(job.id)}
        </span>
      </nav>

      <header className="detail-head">
        <div className="min-w-0">
          <h1 className="page-title">
            <span>{scheduledJobLabel(job.id)}</span>

            <ScheduleJobStatus job={job} />
          </h1>

          <p className="purpose">{job.purpose}</p>

          <div className="meta">
            <span>{`${cadenceLabel(job.schedule.cron)} · ${job.schedule.timeZone}`}</span>

            <code>{job.schedule.cron}</code>

            <code>{`${job.handler.key}@${job.handler.version}`}</code>

            <span className="tnum">Version {job.schedule.version}</span>
          </div>
        </div>

        <div className="actions">
          <button
            className="btn"
            disabled={auth.user?.role !== "admin" || job.manualOnly || actions.pendingId !== null}
            title={job.manualOnly ? "This job is manual only" : undefined}
            onClick={() => actions.toggle(job)}
          >
            {job.enabled ? <Pause className="icon sm" /> : <Play className="icon sm" />}

            {job.enabled ? "Pause" : "Resume"}
          </button>

          <ScheduleRunNow jobId={job.id} />
        </div>
      </header>

      {actions.error ? (
        <p className="banner" role="alert">
          {actions.error}
        </p>
      ) : null}

      <ScheduleRunHealth job={job} />

      <div className="tabs detail-tabs" role="tablist" aria-label="Job sections">
        {tabs.map((tab, index) => (
          <button
            className="tab"
            role="tab"
            key={tab.id}
            id={`tab-${tab.id}`}
            aria-controls={`panel-${tab.id}`}
            aria-selected={active === tab.id}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => keyDown(event, index)}
          >
            {tab.label}

            {"count" in tab && tab.count !== undefined ? (
              <span className="count">{tab.count}</span>
            ) : null}
          </button>
        ))}
      </div>

      <section
        key={active}
        className="panel panel-enter"
        id={`panel-${active}`}
        role="tabpanel"
        aria-labelledby={`tab-${active}`}
      >
        <Outlet />
      </section>
    </>
  )
}

function activeTab(pathname: string) {
  if (pathname.endsWith("/schedule")) {
    return "schedule"
  }
  if (pathname.endsWith("/history")) {
    return "history"
  }
  return "runs"
}
